import { ReactElement, useEffect, useState } from "react";
import { BsCameraVideo } from "react-icons/bs";
import {
  IoArchiveOutline,
  IoImageOutline,
  IoLocationOutline,
} from "react-icons/io5";
import chatSidebarTime from "../../../utils/chatSidebarTime";
import { HiOutlineDocumentText } from "react-icons/hi";

import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuItem,
} from "rctx-contextmenu";
import { socketInstance } from "../../../socket/socket";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import TypingIndicator from "../../../components/TypingIndicator";
import { RiContactsLine } from "react-icons/ri";
import CallInConversationList from "./CallInConversationList";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { truncateSentence } from "../../../utils/truncateSentence";
import TextTranslate from "../../../utils/TextTranslate";
import { CgPoll } from "react-icons/cg";
import { AiOutlineAudio } from "react-icons/ai";
import { OuterChat } from "../../../types/ChatListType";
import { useNavigateToChat } from "../../../hooks/useNavigateToChat";
import ShowSavedName from "../../../components/ShowSavedName";
import { useTheme } from "../../../context/ThemeProvider";
import { HiOutlineGif } from "react-icons/hi2";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";
import ChatLabelsModal from "./ChatLabelsModal";
import { ChatLabel } from "../../../types/ChatListType";
import { LuTrash2 } from "react-icons/lu";
import { MdOutlineBlock, MdOutlineLabel } from "react-icons/md";
import { chatListFullPayload } from "../../../utils/socketQueries";
import {
  formatSystemMessageText,
  isSystemMessageType,
} from "../../../utils/formatSystemMessageText";
import ChatListLastMessagePreview from "../../../components/ChatListLastMessagePreview";

export default function ConversationList({
  searchUser,
}: {
  searchUser: string;
}) {
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const navigateToChat = useNavigateToChat();
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const TypingUserList = useAppSelector((state) => state.TypingUserList);
  const user_id = useAppSelector((state) => state.userData.user_id);
  const ContactList = useAppSelector((state) => state.ContactList);
  const ChatListArray = useAppSelector((state) => state.chatList);
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const translate = useTranslateText();
  const { translate: translateTime, hour12 } = useTimeFormatOptions();
  const is_chatlist_loading = useAppSelector(
    (state) => state.ViewManager.is_chatlist_loading,
  );

  function resolveUserName(u: {
    user_id?: number | null;
    full_name?: string | null;
    user_name?: string | null;
  } | null) {
    if (!u) return "";
    const contact = ContactList?.data?.find((e) => e?.user_id == u.user_id);
    return contact?.name ?? u.full_name ?? u.user_name ?? "";
  }

  function previewSystemMessage(msg: {
    message_type?: string | null;
    message_content?: string | null;
    User?: any;
    ActionedUser?: any;
  }) {
    return formatSystemMessageText(msg, {
      currentUserId: user_id,
      translate,
      resolveName: resolveUserName,
    });
  }

  const [SelectedConversation, setSelectedConversation] = useState<OuterChat>();
  const [showLabelsModal, setShowLabelsModal] = useState(false);

  useEffect(() => {
    socketInstance().emit("archived_chat_list", chatListFullPayload());
  }, []);

  async function blockUser() {
    const is_block = SelectedConversation?.Records[0].blocked_by.includes(
      user_id.toString(),
    );
    sessionStorage.setItem(
      "selectedConversation",
      JSON.stringify(SelectedConversation),
    );
    dispatch(
      updateMessageOptions({
        showModal: true,
        modalName: "block_user",
        title: `Are you sure you want to ${is_block ? "unblock" : "block"} ${SelectedConversation?.PeerUserData.full_name}`,
        description: is_block
          ? "After unblocking, you will receive messages from this user."
          : "After blocking, you will not receive any messages from this user.",
      }),
    );
  }

  async function archiveConversation() {
    // let addToArchive = await postData("add-to-archive", {
    //   chat_id: SelectedConversation?.chat_id,
    // });

    socketInstance().emit("archive_chat", {
      chat_id: SelectedConversation?.Records[0].chat_id,
    });

    // socketInstance().emit("archived_chat_list", {});
    toast.success(translate("Conversation Archived"), {
      position: "bottom-left",
    });
  }

  async function deleteConversation() {
    const chat = SelectedConversation;
    if (!chat?.Records?.[0]) return;
    sessionStorage.setItem("selectedConversation", JSON.stringify(chat));
    const name =
      chat.Records[0].chat_type == "group"
        ? chat.Records[0].group_name
        : chat.PeerUserData.full_name;
    dispatch(
      updateMessageOptions({
        showModal: true,
        modalName: "delete_conversation",
        title: `Are you sure you want to delete this chat with ${name}?`,
        description:
          "The conversation will be removed from your chat list. This action cannot be undone.",
      }),
    );
  }

  const chatMenuItemClass =
    "hover:!bg-dropdownOptionHover !rounded-lg !px-4 !py-1.5 !flex !items-center !justify-between !gap-2 !text-sm !text-darkText";

  const getCurrentLabels = (chat: OuterChat): ChatLabel[] => {
    const participants = chat.Records[0]?.participants;
    if (!participants?.length) return [];
    const myParticipant = participants.find((p) => p.user_id === user_id);
    if (!myParticipant) return [];
    const labels: ChatLabel[] = [];
    [myParticipant.chat_lable_1, myParticipant.chat_lable_2, myParticipant.chat_lable_3].forEach(
      (l) => l && labels.push(l)
    );
    return labels;
  };

  const isBusiness = useAppSelector((state) => state.userData.user_type) === "business";

  return (
    <>
      <div className="flex w-full flex-col overflow-x-hidden pb-16 lg:pb-0">
        {is_chatlist_loading ? (
          <div className="grid h-full place-content-center">
            <ClipLoader size={23} color={theme == "dark" ? "white" : "black"} />
          </div>
        ) : ChatListArray?.Chats.length == 0 ? (
          <div className="grid h-96 place-content-center gap-5">
            <SearchNotFoundAnimation />
            <div>{translate("No Conversations Found")}</div>
          </div>
        ) : (
          ChatListArray?.Chats.filter(
            (chat: OuterChat) =>
              chat.Records[0].group_name
                .toLowerCase()
                .includes(searchUser.toLowerCase()) ||
              chat.PeerUserData.full_name
                .toLowerCase()
                .includes(searchUser.toLowerCase()),
          ).map((e: OuterChat) => {
            // if (e.Records[0].Messages.length == 0) {
            //   return null;
            // }
            const chatId = e.Records[0].chat_id;
            const isSelected =
              currentConversationData.chat_id == e.Records[0].chat_id;
            return (
              <ContextMenuTrigger
                id="my-context-menu-1"
                key={chatId}
                className={`chat-row-trigger${isSelected ? " bg-selectedChat" : ""}`}
                attributes={{
                  style: { display: "block", width: "100%" },
                }}
              >
                  <div
                    onClick={() => {
                      sessionStorage.setItem(
                        "selectedConversation",
                        JSON.stringify(e),
                      );
                      navigateToChat({
                        chat_id: e.Records[0].chat_id,
                        chat_type: e.Records[0].chat_type,
                        full_name: e.PeerUserData.full_name,
                        group_name: e.Records[0].group_name,
                        group_icon: e.Records[0].group_icon,
                        profile_pic: e.PeerUserData.profile_pic,
                        blur_hash:
                          e.Records[0].chat_type == "group"
                            ? e.Records[0].blur_hash ||
                              e.Records[0].blurHash ||
                              ""
                            : e.PeerUserData.blur_hash ||
                              e.PeerUserData.blurHash ||
                              "",
                        user_id: e.PeerUserData.user_id,
                        is_group_blocked: e.Records[0]?.blocked_by?.includes(
                          user_id.toString(),
                        ),
                        other_user_blocked_you:
                          e.Records[0]?.blocked_by?.length >= 1 &&
                          !e.Records[0].blocked_by.includes(user_id.toString()),
                        user_name: e.PeerUserData.user_name,
                        email_id: e.PeerUserData.email,
                        unseen_count: e.Records[0].unseen_count,
                        updatedAt: e.PeerUserData.updatedAt!,
                        is_message_list_empty:
                          e.Records[0].Messages.length === 0,
                        createdAt: e.Records[0].createdAt,
                        bio: e.PeerUserData.bio,
                        group_description: e.Records[0].group_description,
                        mobile_num: e.PeerUserData.mobile_num!,
                        country_code: e.PeerUserData.country_code,
                      });
                    }}
                    onContextMenu={() => {
                      setSelectedConversation(e);
                    }}
                    className={`group flex w-full cursor-pointer items-center gap-3 px-4 py-3 transition hover:bg-selectedChat active:bg-black/[0.03] dark:active:bg-white/[0.04] ${
                      isSelected ? "bg-selectedChat" : ""
                    }`}
                  >
                    <div className="relative h-14 w-14 shrink-0">
                      <LoadingSkeletonImageDynamic
                        radius=""
                        className="h-14 w-14 rounded-full object-cover ring-1 ring-black/5 dark:ring-white/10"
                        image_height="100%"
                        image_url={
                          e.Records[0].chat_type == "group"
                            ? e.Records[0].group_icon
                            : e.PeerUserData.profile_pic
                        }
                        blur_hash={
                          e.Records[0].chat_type == "group"
                            ? e.Records[0].blur_hash || e.Records[0].blurHash
                            : e.PeerUserData.blur_hash ||
                              e.PeerUserData.blurHash
                        }
                        image_width=""
                      />
                      {e.Records[0].chat_type == "private" &&
                        OnlineUserList.onlineUsers.find(
                          (user) => user.user_id == e.PeerUserData.user_id,
                        ) && (
                          <img
                            className="absolute bottom-0 right-0 z-30 h-4 w-4"
                            src="/Home/Online_Green_dot.png"
                            alt=""
                          />
                        )}
                    </div>
                    {/* <img
                    src={e.is_group ? e.group_profile_image : e.profile_image}
                    className="mr-3 h-10 w-10 rounded-full object-cover 2xl:h-12 2xl:w-12"
                    alt=""
                  /> */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-1">
                          <div className="line-clamp-1 text-[16px] font-semibold capitalize text-darkText">
                            {e.Records[0].chat_type == "group" ? (
                              truncateSentence(e.Records[0].group_name, 32)
                            ) : (
                              <ShowSavedName
                                user_id={e.PeerUserData.user_id.toString()}
                                name={truncateSentence(
                                  e.PeerUserData.full_name,
                                  32,
                                )}
                              />
                            )}
                          </div>
                          {getCurrentLabels(e).length > 0 && (
                            <div className="flex shrink-0 gap-1">
                              {getCurrentLabels(e).map((l) => (
                                <span
                                  key={l.lable_id}
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: l.lable_color }}
                                  title={l.lable_text}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 text-[12px] tabular-nums text-lightText">
                          {chatSidebarTime(
                            e.Records[0].Messages.length > 0
                              ? e.Records[0].Messages[0]?.updatedAt
                              : e.Records[0].updatedAt,
                            { translate: translateTime, hour12 },
                          )}
                        </div>
                      </div>

                      <div className="mt-0.5 flex items-center gap-2">
                        {/* <LiaCheckDoubleSolid className="text-lg text-primary" /> */}
                        <div className="line-clamp-1 flex min-w-0 flex-1 gap-x-1 text-[13px] text-lightText">
                          {/* First check for typing ====================================================================================*/}
                          <>
                            {TypingUserList.typingUserList.some(
                              (typing) =>
                                typing.chat_id == e.Records[0].chat_id,
                            ) ? (
                              <TypingIndicator />
                            ) : (
                              <>
                                {e.Records[0].cleared_for?.includes(
                                  user_id.toString(),
                                ) ? (
                                  ""
                                ) : ["image", "gif", "video"].includes(
                                    e.Records[0].Messages[0]?.message_type ||
                                      "",
                                  ) ? (
                                  <ChatListLastMessagePreview
                                    message={e.Records[0].Messages[0]}
                                  />
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "audio" ? (
                                  <>
                                    <AiOutlineAudio className="fa fa-solid fa-image w-5 text-lg text-gray-500" />
                                    <TextTranslate text="Audio" />
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "voice" ? (
                                  <>
                                    <AiOutlineAudio className="fa fa-solid fa-image w-5 text-lg text-gray-500" />
                                    <TextTranslate text="Voice message" />
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "doc" ? (
                                  <>
                                    <HiOutlineDocumentText className="fa fa-solid fa-image w-5 text-lg text-gray-500" />
                                    <TextTranslate text="Document" />
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "location" ? (
                                  <>
                                    <IoLocationOutline className="fa fa-solid fa-image w-5 text-lg text-gray-500" />
                                    <TextTranslate text="Location" />
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "text" ? (
                                  <span className="w-full">
                                    {truncateSentence(
                                      e.Records[0].Messages[0]?.message_content,
                                      32,
                                    )}
                                  </span>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "link" ? (
                                  <>
                                    <span>🔗</span>
                                    <span className="line-clamp-1 w-full">
                                      {truncateSentence(
                                        e.Records[0].Messages[0]
                                          ?.message_content,
                                        32,
                                      )}
                                    </span>
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "contact" ? (
                                  <div className="flex items-center gap-1">
                                    <RiContactsLine />
                                    <span className="line-clamp-1">
                                      <TextTranslate text="Contact" />
                                    </span>
                                  </div>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "poll" ? (
                                  <>
                                    <CgPoll className="text-xl" />
                                    <span className="line-clamp-1">
                                      <TextTranslate text="Poll" />
                                    </span>
                                  </>
                                ) : e.Records[0].Messages[0]?.message_type ==
                                  "call" ? (
                                  <>
                                    <CallInConversationList messageData={e} />
                                  </>
                                ) : isSystemMessageType(
                                    e.Records[0].Messages[0]?.message_type,
                                  ) ? (
                                  <>
                                    {previewSystemMessage(
                                      e.Records[0].Messages[0],
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {e.Records[0].Messages[0]?.message_content}
                                  </>
                                )}
                              </>
                            )}
                          </>
                        </div>
                        {e.Records[0].unseen_count >= 1 ? (
                          <div className="grid h-5 min-w-5 shrink-0 place-content-center rounded-full bg-themeColor px-1.5 text-[11px] font-semibold tabular-nums leading-none text-white">
                            {e.Records[0].unseen_count}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
              </ContextMenuTrigger>
            );
          })
        )}
      </div>

      <ContextMenu id="my-context-menu-1" className="!rounded-xl !bg-modalBg !p-1">
        {(() => {
          const menuItems: ReactElement[] = [];
          if (isBusiness) {
            menuItems.push(
              <ContextMenuItem
                key="labels"
                onClick={() => setShowLabelsModal(true)}
                className={chatMenuItemClass}
              >
                <span>
                  <TextTranslate text="Labels" />
                </span>
                <MdOutlineLabel className="text-xl" />
              </ContextMenuItem>,
            );
          }
          menuItems.push(
            <ContextMenuItem
              key="archive"
              onClick={() => archiveConversation()}
              className={chatMenuItemClass}
            >
              <span>
                <TextTranslate text="Archive Chat" />
              </span>
              <IoArchiveOutline className="text-xl" />
            </ContextMenuItem>,
            <ContextMenuItem
              key="delete"
              onClick={() => deleteConversation()}
              className={chatMenuItemClass}
            >
              <span>
                <TextTranslate text="Delete Chat" />
              </span>
              <LuTrash2 className="text-xl" />
            </ContextMenuItem>,
          );
          if (SelectedConversation?.Records[0].chat_type == "private") {
            menuItems.push(
              <ContextMenuItem
                key="block"
                onClick={() => blockUser()}
                className={chatMenuItemClass}
              >
                <span>
                  <TextTranslate
                    text={
                      SelectedConversation?.Records[0].blocked_by?.includes(
                        user_id.toString(),
                      )
                        ? "Unblock"
                        : "Block"
                    }
                  />
                </span>
                <MdOutlineBlock className="text-xl" />
              </ContextMenuItem>,
            );
          }
          return menuItems;
        })()}
      </ContextMenu>

      {SelectedConversation && (
        <ChatLabelsModal
          chatId={SelectedConversation.Records[0].chat_id}
          isOpen={showLabelsModal}
          onClose={() => setShowLabelsModal(false)}
          currentLabels={getCurrentLabels(SelectedConversation)}
        />
      )}
    </>
  );
}
